import { useId, type FocusEventHandler, type ReactNode } from 'react';

import { errorMessage, type FieldErrorLike } from '@/types/form';

export interface CheckboxProps {
    name: string;
    label?: ReactNode;
    checked?: boolean;
    /** Reçoit un pseudo-événement `{ target: { name, value: boolean } }`, compatible avec `field.onChange`. */
    onChange: (event: { target: { name: string; value: boolean } }) => void;
    onBlur?: FocusEventHandler<HTMLInputElement>;
    error?: FieldErrorLike;
    required?: boolean;
}

const Checkbox = ({ name, label, checked, onChange, onBlur, error, required }: CheckboxProps) => {
    const id = useId();
    const errorId = `${id}-error`;
    return (
        <div>
            <label htmlFor={id} className="flex items-center gap-3 cursor-pointer select-none text-sm">
                <div className="relative flex items-center justify-center">
                    <input
                        type="checkbox"
                        id={id}
                        name={name}
                        checked={checked}
                        onChange={(e) => {
                            onChange({
                                target: {
                                    name,
                                    value: e.target.checked,
                                },
                            });
                        }}
                        onBlur={onBlur}
                        className="peer sr-only"
                        aria-required={required || undefined}
                        aria-invalid={error ? true : undefined}
                        aria-describedby={error ? errorId : undefined}
                    />
                    <div className={`w-5 h-5 rounded-md border-2 transition-all duration-200 flex items-center justify-center ${checked ? 'bg-primary border-primary' : 'border-border bg-card hover:border-primary/50'}`}>
                        {checked && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                        )}
                    </div>
                </div>
                <span>{label}</span>
            </label>
            {error && (
                <p id={errorId} className="mt-1 text-xs text-destructive italic" role="alert" aria-live="assertive">
                    {errorMessage(error)}
                </p>
            )}
        </div>
    );
};

export default Checkbox;
